import { useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { CancellationPolicy } from '@/api';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { Button, Text } from '@/ui';

const MISSING_VALUE = '—';

interface CompareCancellationPoliciesModalProps {
  visible: boolean;
  onClose: () => void;
  cancellationPolicies: CancellationPolicy[];
}

export default function CompareCancellationPoliciesModal({
  visible,
  onClose,
  cancellationPolicies,
}: CompareCancellationPoliciesModalProps) {
  const { theme, styles } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  const close = () => {
    setShowModal(false);
    onClose();
  };

  const sortedPolicies = useMemo(
    () => [...cancellationPolicies].sort((a, b) => a.sortIndex - b.sortIndex),
    [cancellationPolicies]
  );

  const canonicalRows = useMemo(
    () => sortedPolicies[0]?.meta?.comparisonRows ?? [],
    [sortedPolicies]
  );

  const hasTable = sortedPolicies.length > 0 && canonicalRows.length > 0;

  const dividerColor = theme.colors.card.hover.borderColor;

  const cellStyle = {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 12,
    justifyContent: 'flex-start' as const,
  };

  const dividerCellStyle = {
    ...cellStyle,
    borderRightWidth: 1,
    borderRightColor: dividerColor,
  };

  const labelCellStyle = {
    ...dividerCellStyle,
    flex: 0.85,
  };

  const rowStyle = {
    flexDirection: 'row' as const,
    alignItems: 'stretch' as const,
    borderBottomWidth: 1,
    borderBottomColor: dividerColor,
  };

  const lastRowStyle = {
    flexDirection: 'row' as const,
    alignItems: 'stretch' as const,
  };

  const valueFor = (policy: CancellationPolicy, key: string) =>
    policy.meta?.comparisonRows?.find((r) => r.key === key)?.value ??
    MISSING_VALUE;

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
      }}
      onBackdropPress={close}
    >
      <View
        style={{
          backgroundColor: theme.colors.background_2,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          paddingBottom: 24,
          ...(isDesktop() && {
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            paddingBottom: 0,
          }),
        }}
      >
        <View
          style={{
            width: '100%',
            marginTop: 16,
            flexDirection: 'row',
            paddingHorizontal: 16,
            paddingBottom: 16,
            borderBottomColor: theme.colors.card.default.borderColor,
            borderBottomWidth: 1,
          }}
        >
          <Text type="title2">Refundable vs. Non-Refundable</Text>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={close}
            style={{
              position: 'absolute',
              right: 16,
            }}
          />
        </View>

        <View style={{ padding: 16, gap: 16 }}>
          {hasTable ? (
            <View
              style={{
                borderWidth: 1,
                borderColor: theme.colors.card.hover.borderColor,
                borderRadius: 12,
                overflow: 'hidden',
                backgroundColor: theme.colors.card.default.backgroundColor,
              }}
            >
              <View
                style={[
                  rowStyle,
                  { backgroundColor: theme.colors.card.hover.backgroundColor },
                ]}
              >
                <View style={[cellStyle, { flex: 0.85 }]} />
                {sortedPolicies.map((policy) => (
                  <View key={policy.id} style={cellStyle}>
                    <Text type="caption1">{policy.title}</Text>
                  </View>
                ))}
              </View>

              {canonicalRows.map((row, rowIndex) => (
                <View
                  key={row.key}
                  style={
                    rowIndex === canonicalRows.length - 1
                      ? lastRowStyle
                      : rowStyle
                  }
                >
                  <View style={labelCellStyle}>
                    <Text type="caption1Bold">{row.label}</Text>
                  </View>
                  {sortedPolicies.map((policy, policyIndex) => {
                    const isLastColumn =
                      policyIndex === sortedPolicies.length - 1;
                    return (
                      <View
                        key={policy.id}
                        style={isLastColumn ? cellStyle : dividerCellStyle}
                      >
                        <Text
                          type="caption1"
                          style={{ color: theme.colors.text.secondary }}
                        >
                          {valueFor(policy, row.key)}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
          ) : null}

          <Button
            text="Got It"
            type="primary"
            onPress={close}
            style={styles.button}
          />
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet({
  container: {
    margin: 0,
    alignItems: {
      md: 'center',
    },
    justifyContent: {
      xs: 'flex-end',
      md: 'center',
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 1000,
    },
    width: {
      xs: '100%',
      md: 1000,
    },
  },
  button: {
    alignSelf: {
      xs: 'flex-end',
      md: 'flex-end',
    },
    width: {
      xs: '100%',
      md: 150,
    },
  },
});
